import React, { useCallback, useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { IoMdAdd } from "react-icons/io";
import { Delete, Get, Post, Put } from "../../Axios/AxiosFunctions";
import useDebounce from "../../CustomHooks/useDebounce";
import { rolesAndPermissions } from "../../apis/apis";
import Button from "../../components/Button";
import PageHeaderName from "../../components/PageHeaderName/PageHeaderName";
import PaginationComponent from "../../components/PaginationComponent";
import SearchWithButton from "../../components/SearchWithButton/SearchWithButton";
import SideBarSkeleton from "../../components/SideBarSkeleton";
import Table from "../../components/Table";
import { SHOW_RESULTS_OPTIONS } from "../../data/dropdown-options";
import classes from "./RoleAndPermissions.module.css";
// icons
import ScrollContainer from "react-indiana-drag-scroll";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import AddEditPermissionModal from "../../Modals/AddEditPermissionModal";
import AreYouSureModal from "../../Modals/AreYouSureModal";
import { apiHeader } from "../../config/apiUrl";
import {
  DeleteIcon,
  EditIcon,
} from "../../constant/imagePath";
import { getFormattedLabel, mergeClass } from "../../utils/helper";
import RenderToast from "../../components/RenderToast";
import Tooltip from "@/components/Tooltip";
import DataTable from "@/components/NewTable";
import useUrlParams from "@/hooks/useUrlParams";
import { useGet } from "@/apiHooks/HttpFunctions";
import { columns } from "./Columns";
import { PER_PAGE } from "@/constant/constants";
import { CONFIRMATION_MODAL_TYPES } from "@/Modals/NewConfirmationModal/constants";
import { useModalConfirmation } from "@/context/ModalConfirmationContext";

export default function RoleAndPermissions() {
  const { accessToken, allPermissions } = useSelector(
    (state) => state?.authReducer
  );
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState("");
  const [show, setShow] = useState(null);
  const [selectedColumn] = useState(dummySelectedColumn);
  const debounceSearch = useDebounce(search, 500);
  const [recordLimit, setRecordLimit] = useState(SHOW_RESULTS_OPTIONS[0]);
  const [permissions, setPermissions] = useState([]);
  const [isAddOrEditApiCalling, setIsAddOrEditApiCalling] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // getData
  const getData = async ({
    pg = page,
    _search = debounceSearch,
    limit = recordLimit,
  }) => {
    setLoading(true);

    const [responseData, isSuccess] = await Get({
      route: `${rolesAndPermissions}?page=${pg}&filter[keyword]=${_search}&perPage=${limit?.value}`,
      accessToken,
    });

    if (isSuccess) {
      setData(responseData?.data?.data);
      setTotalRecords(responseData?.data?.meta?.total);
      setPage(pg);
      setSelectedItem(null);
    }
    setLoading(false);
  };

  // getAllPermissions
  const getAllPermissions = async () => {
    const url = `admin/permissions`;
    const [responseData, isSuccess] = await Get({
      route: url,
      accessToken,
    });

    if (isSuccess) {
      setPermissions(responseData?.data?.data);
    }
  };

  // addOrEditRole
  const addOrEditRole = async (values) => {
    const url = !selectedItem
      ? `admin/roles`
      : `admin/roles/${selectedItem?.ids}`;
    const fn = !selectedItem ? Post : Put;

    setIsAddOrEditApiCalling(true);
    const [responseData, isSuccess] = await fn({
      route: url,
      data: values,
      headers: apiHeader(accessToken),
    });

    if (isSuccess) {
      setShow(null);
      setPage(1);
      await getData({
        pg: 1,
        _search: debounceSearch,
        limit: recordLimit,
      });
      RenderToast({
        type: "success",
        message: `Role ${selectedItem ? "updated" : "added"} Successfully`,
      });
      setSelectedItem(null);
    }

    setIsAddOrEditApiCalling(false);
  };

  // deleteRoleHandler
  const deleteRoleHandler = async () => {
    const url = `admin/roles/${selectedItem?.ids}`;

    setIsAddOrEditApiCalling(true);
    const [responseData, isSuccess] = await Delete({
      route: url,
      headers: apiHeader(accessToken),
    });

    if (isSuccess) {
      setShow(null);
      await getData({
        pg: page,
        _search: debounceSearch,
        limit: recordLimit,
      });
      RenderToast({
        type: "success",
        message: "Role Deleted Successfully",
      });
      setSelectedItem(null);
    }
    setIsAddOrEditApiCalling(false);
  };

  useEffect(() => {
    getAllPermissions();
  }, []);

  useEffect(() => {
    getData({ pg: 1, _search: debounceSearch, limit: recordLimit });
  }, [debounceSearch, recordLimit]);

  useEffect(() => {
    if (!show) {
      setSelectedItem(null);
    }
  }, [show]);

  let tableHeadings = [
    ...(actions.some(({ permission }) => allPermissions.includes(permission))
      ? [
          {
            id: "actions",
            label: "Actions",
            style: { minWidth: "100px" },
            renderValue: (a, i) => {
              return (
                <div className={classes.actionsDiv}>
                  {actions
                    ?.filter(({ permission }) =>
                      allPermissions.includes(permission)
                    )
                    .map((actionData) => {
                      let {
                        src,
                        alt,
                        className,
                        modalStatus,
                        toolTipText,
                        ...rest
                      } = actionData;
                      if (rest?.iconWithStatus) {
                        let iconData =
                          rest?.iconWithStatus.find(
                            (e) => e.status === data[i]?.status
                          ) || {};
                        src = iconData?.src;
                        alt = iconData?.alt;
                        toolTipText = iconData?.toolTipText;
                        className = iconData?.className;
                      }
                      return (
                        <Tooltip tooltipText={toolTipText}>
                          <div
                            className={mergeClass(
                              className,
                              classes.iconStyling
                            )}
                            key={alt}
                            onClick={() => {
                              setSelectedItem(data[i]);
                              setShow(modalStatus);
                            }}
                            tooltipText={toolTipText}
                          >
                            <img src={src} alt={alt} />
                          </div>
                        </Tooltip>
                      );
                    })}
                </div>
              );
            },
          },
        ]
      : []),

    ...selectedColumn?.map((e, i) => ({
      id: e,
      label: getFormattedLabel(e),
    })),
  ];

  let tableData = data?.map((e) => {
    return selectedColumn?.map((j) => {
      return {
        [j]:
          j === "permission_names"
            ? e[j]?.join("; ") || "-"
            : typeof e[j] == "object"
            ? Array.isArray(e[j])
              ? e[j]?.map((e) => e?.name).join(", ") || "-"
              : e[j]?.name || "-"
            : e[j] || "-",
      };
    });
  });

  let modifiedTableData = tableData?.map((row) => {
    return row?.reduce((acc, curr) => {
      return { ...acc, ...curr };
    }, {});
  });

  const params = useUrlParams();
  const { openModalConfirmation } = useModalConfirmation();

  const { data: listData, isPending: listIsPending } = useGet({
    apiURL: rolesAndPermissions,
    params: {
      page: params.page || 1,
      "filter[keyword]": params["filter[keyword]"] ?? "",
      perPage: params.perPage || PER_PAGE,
    },
  });

  const handleModalAction = useCallback(
    (type, data) => {
      openModalConfirmation({
        type,
        title: data?.name,
        id: data?.id,
        apiURL: rolesAndPermissions,
      });
    },
    [openModalConfirmation, rolesAndPermissions]
  );

  const handleOnClickDelete = (data) =>
    handleModalAction(CONFIRMATION_MODAL_TYPES.DELETE, data);

  return (
    <>
      <DataTable
        data={listData}
        columns={columns(handleOnClickDelete)}
        isLoading={listIsPending}
        searchKey="filter[keyword]"
        addData={{
          onClick: () => {
            console.log("ADD BUTTON CLICKED");
          },
        }}
      />

      {(show === "confirmStatus" || show === "delete-item") && (
        <AreYouSureModal
          setShow={setShow}
          show={show === "confirmStatus" || show === "delete-item"}
          title={`Are you sure you want to ${
            show === "delete-item" ? "delete" : "deactivate"
          } `}
          userName={`${selectedItem?.name} Permission`}
          onClick={deleteRoleHandler}
          isApiCall={isAddOrEditApiCalling}
        />
      )}

      {/* <Container fluid>
        <Row>
          <Col md={12}>
            <SearchWithButton
              search={search}
              setSearch={setSearch}
              className={classes.input}
              clearButton={() => {
                setPage(1);
                setSearch("");
              }}
              {...(allPermissions.includes("role.create") && {
                onClickAddRole: () => setShow("AddEditPermissionModal"),
              })}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <div className={classes.table}>
              <ScrollContainer>
                <Table
                  loading={loading}
                  tableData={modifiedTableData}
                  headings={tableHeadings}
                />
              </ScrollContainer>
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <PaginationComponent
              options={SHOW_RESULTS_OPTIONS}
              setValue={setRecordLimit}
              value={recordLimit}
              totalPages={Math.ceil(totalRecords / recordLimit?.value)}
              currentPage={page}
              setCurrentPage={(p) => {
                getData({
                  pg: p,
                });
              }}
            />
          </Col>
        </Row>
      </Container> */}
      {/* modals */}
      {/* {(show === "confirmStatus" || show === "delete-item") && (
        <AreYouSureModal
          setShow={setShow}
          show={show === "confirmStatus" || show === "delete-item"}
          title={`Are you sure you want to ${
            show === "delete-item" ? "delete" : "deactivate"
          } `}
          userName={`${selectedItem?.name} Permission`}
          onClick={deleteRoleHandler}
          isApiCall={isAddOrEditApiCalling}
        />
      )} */}

      {/* {show === "AddEditPermissionModal" && (
        <AddEditPermissionModal
          setShow={setShow}
          show={show === "AddEditPermissionModal"}
          title={"Role"}
          permissions={permissions}
          handleSubmit={addOrEditRole}
          isLoading={isAddOrEditApiCalling}
          selectedItem={selectedItem}
        />
      )} */}
    </>
  );
}

let dummySelectedColumn = ["name", "permission_names"];

const actions = [
  {
    src: EditIcon,
    alt: "editIcon",
    permission: "role.edit",
    className: "blue-gradient",
    modalStatus: "AddEditPermissionModal",
    toolTipText: "Edit Role",
  },

  {
    src: DeleteIcon,
    alt: "deleteUserIcon",
    permission: "role.delete",
    className: classes.deleteUserIcon,
    modalStatus: "delete-item",
    toolTipText: "Delete Role",
  },
];
